// SIGMET (Significant Meteorological Information) layer family.
//
// Significant meteorological hazards: convective, severe turbulence, severe
// icing, ash. Heavier visual weight than AIRMET. One GeoJSON source drives
// two layers: a hazard-coded fill (0.25 opacity) and a solid hazard-coded
// border line. Both layers are mounted with visibility: 'none' (lazy toggle).
//
// Extracted from addOverlays.ts as HI-1 batch 7 round 2 (2026-05-24).
// Pure mechanical move — source spec, paint expressions, layer ids, and
// layer order are byte-identical to the pre-extraction form.
//
// applyState reads visibility from useUiStore so a basemap setStyle()
// rebuild restores the user's current toggle state transparently.

import type { Map as MlMap } from 'maplibre-gl';
import { EMPTY_FC } from '../MapView';
import { registerLayerFamily } from '../layerVisibilityRegistry';
import { useLayerVisibilityStore } from '../../stores/layerVisibilityStore';
import { useUiStore } from '../../stores/uiStore';
import { applyValidityWindowFilter } from '../mapFilters';
import { isOverlayLoaded, markOverlayLoaded, refreshOverlay } from '../overlayRuntime';
import { OVERLAY_BY_ID } from '../overlaySpecs';

export function addSigmetLayers(map: MlMap): void {
  if (!map.getSource('sigmet')) {
    map.addSource('sigmet', { type: 'geojson', data: EMPTY_FC });
    map.addLayer({
      id: 'sigmet-fill',
      type: 'fill',
      source: 'sigmet',
      layout: { visibility: 'none' },
      paint: {
        'fill-color': [
          'match', ['get', 'hazard'],
          'CONVECTIVE', '#dc2626',
          'TURB', '#ea580c',
          'ICE', '#0891b2',
          'IFR', '#3b82f6',
          'MTN OBSCN', '#a78bfa',
          'ASH', '#7f1d1d',
          /* default */ '#dc2626',
        ],
        'fill-opacity': 0.25,
      },
    });
    map.addLayer({
      id: 'sigmet-line',
      type: 'line',
      source: 'sigmet',
      layout: { visibility: 'none' },
      paint: {
        'line-color': [
          'match', ['get', 'hazard'],
          'CONVECTIVE', '#dc2626',
          'TURB', '#ea580c',
          'ICE', '#0891b2',
          'IFR', '#3b82f6',
          'MTN OBSCN', '#a78bfa',
          'ASH', '#7f1d1d',
          /* default */ '#dc2626',
        ],
        'line-width': 2.5,
      },
    });
  }
}

registerLayerFamily({
  id: 'sigmet',
  label: 'SIGMET',
  sourceId: 'sigmet',
  layerIds: ['sigmet-fill', 'sigmet-line'],
  mount: (map) => addSigmetLayers(map),
  visibilitySelector: (l) => l.wxhazards && l.sigmet,
  applyState: (map) => {
    if (!map.getLayer('sigmet-fill')) return;
    const layers = useLayerVisibilityStore.getState().layers;
    const show = layers.wxhazards && layers.sigmet;
    const v = show ? 'visible' : 'none';
    map.setLayoutProperty('sigmet-fill', 'visibility', v);
    map.setLayoutProperty('sigmet-line', 'visibility', v);
    // Re-apply validity window filter (time-scrubber) so basemap-rebuild
    // and wxHazardOffsetH slider changes both keep the filter current.
    if (show) {
      const offsetH = useUiStore.getState().wxHazardOffsetH;
      applyValidityWindowFilter(map, ['sigmet-fill', 'sigmet-line'], offsetH);
    }
  },
  onToggleOn: (map) => {
    const spec = OVERLAY_BY_ID.get('sigmet');
    if (!spec || isOverlayLoaded('sigmet')) return;
    markOverlayLoaded('sigmet');
    return refreshOverlay(map, spec);
  },
});
